import dayjs from "dayjs";
import { EditionKind } from "./types";
import { MeetingTool } from "./SessionWizard/types";

const toIds = (items?: any): number[] =>
  Array.isArray(items)
    ? items
        .map((it) => Number(it && typeof it === "object" ? it.id : it))
        .filter((n) => !Number.isNaN(n))
    : [];

const bool = (v: any): boolean => v === true;

const siteToId = (site: any): number | null => {
  if (site === null || site === undefined) return null;
  if (Array.isArray(site)) return site[0] ? Number(site[0].id) : null;
  if (typeof site === "object") return Number(site.id);
  const n = Number(site);
  return Number.isNaN(n) ? null : n;
};

const buildOnlineMeeting = (
  tool: MeetingTool | null | undefined,
  s: any,
  publishDate?: string | number | null,
) => {
  const triggerExternal =
    (bool(s.trigger_external_services) &&
      !!publishDate &&
      dayjs().isAfter(dayjs(publishDate))) ||
    !!s.online_meeting_updated_at;

  const base = {
    tool: tool || null,
    trigger_external_services: triggerExternal,
    confirmed:
      bool(s.confirmed) &&
      (bool(s.trigger_external_services) || tool === "OTHER"),
  };

  if (tool === "MSTEAMS") {
    return {
      ...base,
      roles: {
        PRODUCER: toIds(s.producer),
        PRESENTER: toIds(s.presenter),
        COORGANIZER: toIds(s.coorganizer ?? s.co_organizer),
      },
      settings: {
        lobby_settings: {
          is_dial_in_bypass_enabled: bool(s.is_dial_in_bypass_enabled),
          scope: s.scope || null,
        },
        allow_attendee_to_enable_camera: bool(
          s.allow_attendee_to_enable_camera,
        ),
        allow_attendee_to_enable_mic: bool(s.allow_attendee_to_enable_mic),
        allow_breakout_rooms: bool(s.allow_breakout_rooms),
        allow_meeting_chat: s.allow_meeting_chat || null,
        allow_recording: bool(s.allow_recording),
        allow_transcription: bool(s.allow_transcription),
        record_automatically: bool(s.record_automatically),
        allowed_presenters: s.allowed_presenters || null,
      },
    };
  }

  if (tool === "GMEET") {
    return {
      ...base,
      roles: { COHOST: toIds(s.cohost) },
      settings: {
        lobby_settings: { access_type: s.access_type || null },
        chat_restriction: s.chat_restriction || null,
        default_join_as_viewer_type: bool(s.default_join_as_viewer_type),
        auto_transcription_generation: bool(s.auto_transcription_generation),
        auto_recording_generation: bool(s.auto_recording_generation),
      },
    };
  }

  return { ...base, settings: { link: s.link || null } };
};

const mapVirtualSession = (s: any, publishDate?: string | number | null) => ({
  id: s?.id ?? null,
  start_date: dayjs(s?.start_date).format("YYYY-MM-DDTHH:mm:ssZ"),
  end_date: dayjs(s?.end_date).format("YYYY-MM-DDTHH:mm:ssZ"),
  faculties: toIds(s?.faculties),
  tutors: toIds(s?.tutors),
  online_meeting: buildOnlineMeeting(s?.tool, s, publishDate),
});

const mapPhysicalSession = (s: any, publishDate?: string | number | null) => ({
  id: s?.id ?? null,
  start_date: dayjs(s?.start_date).format("YYYY-MM-DDTHH:mm:ssZ"),
  end_date: dayjs(s?.end_date).format("YYYY-MM-DDTHH:mm:ssZ"),
  publish_date_session: publishDate
    ? dayjs(publishDate).format("YYYY-MM-DD")
    : null,
  faculties: toIds(s?.faculties),
  tutors: toIds(s?.tutors),
  site: siteToId(s?.site),
  rooms: toIds(s?.rooms),
  "QR-code": bool(s?.qrCode ?? s?.["QR-code"]),
  confirmed: bool(s?.confirmed),
});

export const mapSession = (
  s: any,
  kind: EditionKind,
  publishDate?: string | number | null,
) =>
  kind === "virtual"
    ? mapVirtualSession(s, publishDate)
    : mapPhysicalSession(s, publishDate);

export interface EditionPayloadForm {
  lo_id?: number | string | null;
  edition_code?: string | null;
  edition_name?: string | null;
  extra_info?: string | null;
  max_capacity?: number | string | null;
  publish_date?: string | number | null;
}

export const buildEditionPayload = (
  form: EditionPayloadForm,
  sessions: any[],
  kind: EditionKind,
) => ({
  lo_id:
    form.lo_id != null && form.lo_id !== "" ? Number(form.lo_id) : undefined,
  edition_code: form.edition_code ?? null,
  edition_name: form.edition_name ?? null,
  extra_info: form.extra_info ?? null,
  max_capacity:
    form.max_capacity != null && form.max_capacity !== ""
      ? Number(form.max_capacity)
      : null,
  publish_date: form.publish_date
    ? dayjs(form.publish_date).format("YYYY-MM-DD")
    : null,
  sessions: (sessions || []).map((s) => mapSession(s, kind, form.publish_date)),
});
